/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpModule
 */

import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';

import { Injectable, Injector } from '@angular/core';
import { Auth } from 'ajs/modules/core/services/auth';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Constant for 'reload-permissions' header.
 */
const RELOAD_PERMISSIONS = 'reload-permissions';

/**
 * @description
 *      Interceptor to look for `reload-permissions` in response headers,
 *      if found reloads user permissions.
 *
 * @author Aravindh Nagrajan
 */
@Injectable()
export class ProcessReloadPermissionHeaderInterceptor implements HttpInterceptor {
    /**
     * Using Injector here instead of directly injecting Auth to avoid Angular circular
     * dependencies.
     */
    constructor(private readonly injector: Injector) {}

    /** @override */
    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req)
            .pipe(
                tap((event: HttpEvent<any>): void => {
                    if (event instanceof HttpResponse) {
                        const { headers } = event;

                        if (headers.has(RELOAD_PERMISSIONS)) {
                            const reloadPermissionsKey = headers.get(RELOAD_PERMISSIONS);

                            this.processReloadPermissionsHeader(reloadPermissionsKey);
                        }
                    }
                }),
            );
    }

    /**
     * Checks whether current reloadPermissionsKey is unaddressed, if its unaddressed,
     * triggers Auth#updateReloadPermissionsRequestHash call.
     */
    private processReloadPermissionsHeader(reloadPermissionsKey: string): void {
        // Should reload context if true.
        const unaddressedReloadRequest =
            Auth.isNewReloadPermissionsRequest(reloadPermissionsKey);

        if (unaddressedReloadRequest) {
            const authService = this.injector.get(Auth);

            // we mark key as processed right away so that other HTTP calls
            // won't cause multiple reloadUserProfile calls simultaneously for the same value
            Auth.updateReloadPermissionsRequestHash(reloadPermissionsKey, true);

            authService.reloadUserProfileAndPermissions()
                .catch(() => {
                    Auth.updateReloadPermissionsRequestHash(reloadPermissionsKey, false);
                });
        }
    }
}
